import { useState } from "react";
import type { SubmitEvent } from "react";

import { Button, Container, ErrorSummary, PortalLayout, Stack, TextField, Typography } from "../../design-system";
import { readSignInOrigin, readSignInRef, requestSignIn } from "../../portal-auth";
import { signInContent } from "./content";

const EMAIL_FIELD_ID = "sign-in-email";

//: A single, unquantified character class with no repetition of its own -- this can never
//: backtrack, let alone super-linearly, regardless of input length or content.
const WHITESPACE_PATTERN = /\s/;

/**
 * A syntax gate only (AC-21), not an exhaustive email grammar: one "@", a non-blank local part and
 * domain either side, and at least one "." somewhere strictly between the domain's first and last
 * characters.
 *
 * Deliberately not a single regex (typescript:S8786): the equivalent single pattern,
 * `/^[^\s@]+@[^\s@]+\.[^\s@]+$/`, lets its second and third `+` groups both match ".", so a long
 * run of dots with no "@" forces the engine to try every split between them before failing --
 * super-linear backtracking. Splitting the "exactly one @" check, the two no-whitespace checks and
 * the "has an interior dot" check into separate, independently-linear steps accepts and rejects
 * exactly the same strings without that ambiguity: each step is O(n) and none of them re-examines
 * a character the others already ruled on.
 */
function isSyntacticallyValidEmail(value: string): boolean {
  const atIndex = value.indexOf("@");
  if (atIndex === -1 || value.includes("@", atIndex + 1)) {
    return false;
  }
  const local = value.slice(0, atIndex);
  const domain = value.slice(atIndex + 1);
  if (local.length === 0 || domain.length === 0) {
    return false;
  }
  if (WHITESPACE_PATTERN.test(local) || WHITESPACE_PATTERN.test(domain)) {
    return false;
  }
  // At least one "." with >=1 character before it and >=1 character after it within domain --
  // equivalent to a "." somewhere in domain.slice(1, -1) (a plain substring search, not a regex).
  return domain.slice(1, -1).includes(".");
}

/** AC-21: an empty field and a syntactically invalid address are two
 * distinct messages -- never collapsed into one shared string. */
function validateEmail(trimmed: string): string | undefined {
  if (trimmed.length === 0) {
    return signInContent.emptyFieldError;
  }
  if (!isSyntacticallyValidEmail(trimmed)) {
    return signInContent.invalidFormatError;
  }
  return undefined;
}

export interface SignInPageProps {
  /** Called once the sign-in request has been accepted (or has failed in
   * a way AC-21 requires to look the same as acceptance) -- navigates to
   * /sign-in/check-email. */
  onRequested: () => void;
}

/**
 * /sign-in (design state 1.1, AC-21, AC-29). Exactly one email field and
 * one primary action, rebuilt in `PortalLayout` with TG-43's copy.
 *
 * ADR-20261008-evet: the old `?error=link_unavailable` banner is removed --
 * every rejected link now renders the dedicated `/sign-in/link-unavailable`
 * view (AC-24) instead of a banner on this form, which carries an email
 * field AC-24 forbids on that view.
 *
 * Client-side validation (AC-21) never sends a request for an empty or
 * syntactically invalid address: `handleSubmit` returns before calling
 * `requestSignIn` in both cases. A valid address always reaches the
 * check-your-email view, including when `requestSignIn` itself throws (a
 * transport failure) -- a failure here discloses nothing this page does
 * not already withhold (AC-1).
 */
export function SignInPage({ onRequested }: Readonly<SignInPageProps>) {
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const trimmed = email.trim();
    const validationError = validateEmail(trimmed);
    if (validationError !== undefined) {
      setFieldError(validationError);
      return;
    }

    setFieldError(undefined);
    setSubmitting(true);
    try {
      // ADR-20261015-xn9h, ADR-20261102-k4rf: read at submit time from the URL, so a refresh
      // keeps the origin marker and the renewal reference.
      await requestSignIn(
        trimmed,
        readSignInOrigin(window.location.search),
        readSignInRef(window.location.search),
      );
    } catch {
      // AC-21: "whatever the request's outcome, including a 5xx response
      // or no response" -- a transport failure still shows check-your-email.
    } finally {
      setSubmitting(false);
    }
    onRequested();
  }

  return (
    <PortalLayout headerVariant="entry" helpLine={signInContent.helpLine} background="white">
      <Container layout="entry">
        <form onSubmit={(event) => void handleSubmit(event)} noValidate>
          <Stack gap="field">
            {fieldError !== undefined ? (
              <ErrorSummary items={[{ fieldId: EMAIL_FIELD_ID, text: fieldError }]} />
            ) : null}
            <Typography variant="display" as="h1">
              {signInContent.heading}
            </Typography>
            <Typography variant="bodyLarge">{signInContent.body}</Typography>
            <TextField
              id={EMAIL_FIELD_ID}
              label={signInContent.emailFieldLabel}
              help={signInContent.emailFieldHint}
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
              error={fieldError !== undefined}
              {...(fieldError !== undefined ? { errorText: fieldError } : {})}
              fullWidth
            />
            <Button emphasis="primary" type="submit" disabled={submitting} size="large" fullWidth>
              {signInContent.continueActionLabel}
            </Button>
          </Stack>
        </form>
      </Container>
    </PortalLayout>
  );
}
